import React, { useEffect, useState } from 'react';
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import ApiService from "../../../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import DateRangePicker from "../../../components/DateRangePicker";
import { hasPermission } from "../../../utils/permissions";
import { formatDate, formatDurationShort } from "../../../utils/helper";

/**
 * Attendance logs — every admin check-in / check-out session.
 *
 * Rows still open (no check-out yet) show a live-running duration so the screen
 * matches what the header widget is showing that admin.
 */
const List = () => {

    const [isLoader, setIsLoader] = useState(false);
    const [logs, setLogs] = useState([]);
    const [summary, setSummary] = useState({ total_sessions: 0, total_seconds: 0, open_sessions: 0 });
    const [pagination, setPagination] = useState({});
    // Ticks once a second purely so open sessions keep counting up on screen.
    const [now, setNow] = useState(Date.now());
    const [apiData, setApiData] = useState({
        search: "",
        daterange: "",
        status: "",
        page: 1,
    });

    const canDelete = hasPermission('attendance.delete');

    useEffect(() => {
        getLogs();
    }, [apiData.page, apiData.search, apiData.daterange, apiData.status]); // Dependency array for re-fetching data

    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    const getLogs = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "admin/attendance",
                data: apiData
            });
            const data = response.data;
            if (data.status) {
                setLogs(data.data);
                setSummary(data.summary || { total_sessions: 0, total_seconds: 0, open_sessions: 0 });
                setPagination(data.pagination);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
        }
    };

    const handlePageChange = (event) => {
        setApiData(prev => ({ ...prev, page: event }));
    };

    const clearFilter = () => {
        setApiData({
            search: '',
            daterange: '',
            status: '',
            page: 1
        });
    };

    const delData = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'you will not be able to revert it!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            const response = await ApiService.request({
                method: "DELETE",
                url: `admin/attendance/${id}`,
            });
            const data = response.data;
            if (data.status) {
                getLogs();
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        }
    };

    /** Stored duration for a closed session; time-so-far for an open one. */
    const sessionSeconds = (log) => {
        if (log.check_out_at) return log.duration_seconds || 0;
        return Math.max(0, Math.floor((now - new Date(log.check_in_at).getTime()) / 1000));
    };

    const isFiltered = apiData.search || apiData.daterange || apiData.status;

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{("Attendance Logs")}</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">{("Attendance Logs")}</li>
                        </ul>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">

                    {/* Totals for the current filter, not just this page */}
                    <div className="row g-4 mb-5 attendance-summary">
                        <div className="col-md-4">
                            <div className="card h-100">
                                <div className="card-body d-flex align-items-center py-4">
                                    <i className="bi bi-clock-history fs-2x text-primary me-4"></i>
                                    <div>
                                        <div className="fs-3 fw-bold text-dark">{summary.total_sessions}</div>
                                        <div className="fs-7 text-gray-500">Total Sessions</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card h-100">
                                <div className="card-body d-flex align-items-center py-4">
                                    <i className="bi bi-hourglass-split fs-2x text-success me-4"></i>
                                    <div>
                                        <div className="fs-3 fw-bold text-dark">{formatDurationShort(summary.total_seconds)}</div>
                                        <div className="fs-7 text-gray-500">Total Time Logged</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card h-100">
                                <div className="card-body d-flex align-items-center py-4">
                                    <i className="bi bi-person-check-fill fs-2x text-warning me-4"></i>
                                    <div>
                                        <div className="fs-3 fw-bold text-dark">{summary.open_sessions}</div>
                                        <div className="fs-7 text-gray-500">Currently Checked In</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <form method="get">
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="d-flex align-items-center position-relative my-1">
                                                <input type="text" value={apiData.search} onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })} className="form-control form-control-solid me-5"
                                                    placeholder={("Search name, email..")} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <DateRangePicker
                                                value={apiData.daterange}
                                                onChange={(daterange) => setApiData({ ...apiData, daterange, page: 1 })}
                                                placeholder="Select Date Range"
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <select
                                                className="form-select form-select-solid my-1"
                                                value={apiData.status}
                                                onChange={e => setApiData({ ...apiData, status: e.target.value, page: 1 })}
                                            >
                                                <option value="">All Sessions</option>
                                                <option value="open">Checked In</option>
                                                <option value="closed">Checked Out</option>
                                            </select>
                                        </div>
                                        <div className="col-md-2 mt-2">
                                            <button type="button" onClick={clearFilter} className={`btn btn-sm btn-secondary mx-1 ${!isFiltered ? 'd-none' : ''}`}>{('clear')}</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <div id="kt_attendance_table_wrapper" className="dataTables_wrapper dt-bootstrap4 no-footer">
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                        <thead>
                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                <th>{("User")}</th>
                                                <th>{("Date")}</th>
                                                <th>{("Check In")}</th>
                                                <th>{("Check Out")}</th>
                                                <th>{("Duration")}</th>
                                                <th>{("Status")}</th>
                                                {canDelete && <th>{("Actions")}</th>}
                                            </tr>
                                        </thead>
                                        <tbody className="fw-semibold text-gray-600 fs-6">
                                            {logs.length > 0 ? logs.map((log, index) => (
                                                <tr key={log._id || index}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <img src={log.admin_id?.picture} alt="user" className='w-40px h-40px rounded-circle me-3' />
                                                            <div>
                                                                <div className="text-dark fw-bold">{log.admin_id?.name || 'Deleted User'}</div>
                                                                <div className="text-muted fs-7">{log.admin_id?.email}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{formatDate(log.check_in_at, 'MMM D, YYYY')}</td>
                                                    <td>{formatDate(log.check_in_at, 'hh:mm A')}</td>
                                                    <td>
                                                        {log.check_out_at
                                                            ? formatDate(log.check_out_at, 'hh:mm A')
                                                            : <span className="text-muted">—</span>}
                                                    </td>
                                                    <td>{formatDurationShort(sessionSeconds(log))}</td>
                                                    <td>
                                                        <span className={`badge ${log.check_out_at ? 'badge-light-primary' : 'badge-light-success'}`}>
                                                            {log.check_out_at ? 'Checked Out' : 'Checked In'}
                                                        </span>
                                                    </td>
                                                    {canDelete && (
                                                        <td className="mt-2">
                                                            <button onClick={() => delData(log._id)} className="btn btn-sm h-35px btn-light-danger">
                                                                <i className="bi bi-trash-fill fs-6"></i>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan={canDelete ? 7 : 6} className="text-dark text-center mt-2">No data found!!</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {isLoader && (
                                        <Loader text="Loading" size="30" className="text-center" />
                                    )}

                                    {pagination.total_pages > 1 && (
                                        <Paginator
                                            page={pagination.current_page}
                                            rows={pagination.per_page}
                                            totalRecords={pagination.total_records}
                                            onPageChange={handlePageChange}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
